import { getFormProps, getInputProps, useForm } from '@conform-to/react';
import { getZodConstraint, parseWithZod } from '@conform-to/zod';
import type { ActionFunctionArgs, LoaderFunctionArgs } from '@remix-run/node';
import { json } from '@remix-run/node';
import { Form, useActionData } from '@remix-run/react';
import { z } from 'zod';
import { Button } from '~/components/Button';
import { CardBase, CardBody, CardHeader } from '~/components/Card';
import { Label } from '~/components/Forms/Label';
import TextField, { TextFieldWrapper } from '~/components/Forms/TextField';
import LadderLogo from '~/components/LadderLogo';
import { requireAnonymousUser } from '~/server/auth/authenticate-user';
import {
  generatePasswordResetToken,
  getPasswordResetTokenExpirationDate,
} from '~/server/auth/password.server';
import { prisma } from '~/server/db.server';
import { emailProvider } from '~/server/email-provider/email-provider';
import { delay, useIsPending } from '~/utils/misc';
import { getBasePathFromRequest } from '~/utils/request.server';
import { EmailSchema } from '~/utils/user-validation';

const RequestResetPasswordFormSchema = z.object({
  email: EmailSchema,
});

export async function loader({ request }: LoaderFunctionArgs) {
  await requireAnonymousUser(request); // Only see this page if not logged in
  return {};
}

export async function action({ request }: ActionFunctionArgs) {
  await requireAnonymousUser(request);
  const formData = await request.formData();
  const submission = parseWithZod(formData, {
    schema: RequestResetPasswordFormSchema,
  });
  if (submission.status !== 'success') {
    return json(
      { success: false, result: submission.reply() },
      { status: 400 }
    );
  }
  const user = await prisma.users.findFirst({
    where: {
      email: submission.value.email,
    },
    select: {
      id: true,
      email: true,
      firstName: true,
      name: true,
    },
  });

  if (!user) {
    // protect against timing attacks
    // don't reveal if an account with that email exists
    await delay(Math.random() * 1000);
    return {
      success: true,
      result: submission.reply(),
      message: 'If an account with that email exists, we sent you an email',
    };
  }

  // invalidate all existing tokens
  await prisma.resetPasswordTokens.updateMany({
    where: {
      userId: user.id,
    },
    data: {
      used: true,
    },
  });

  // issue new token
  const { token } = await prisma.resetPasswordTokens.create({
    data: {
      userId: user.id,
      expiresAt: getPasswordResetTokenExpirationDate(),
      token: generatePasswordResetToken(),
    },
  });
  await emailProvider.sendPasswordResetEmail({
    baseUrl: getBasePathFromRequest(request),
    resetToken: token,
    user,
  });
  return {
    success: true,
    result: submission.reply(),
    message: 'If an account with that email exists, we sent you an email.',
  };
}

export default function LoginPage() {
  const actionData = useActionData<typeof action>();
  const isPending = useIsPending();
  const [form, fields] = useForm({
    id: 'request-password-reset-form',
    constraint: getZodConstraint(RequestResetPasswordFormSchema),
    lastResult: actionData?.result,
    onValidate({ formData }) {
      return parseWithZod(formData, { schema: RequestResetPasswordFormSchema });
    },
    shouldRevalidate: 'onBlur',
  });

  return (
    <CardBase className="w-full min-w-[416px] max-w-xl p-4">
      <CardHeader>
        <LadderLogo size="md" />
      </CardHeader>
      <CardBody>
        <div>
          <h2 className="text-h2">Reset Password</h2>
        </div>
        {form?.errors && <div className="text-red-500">{form.errors}</div>}
        {isSuccess(actionData) && (
          <div className="text-green-500 max-w-xs">{actionData.message}</div>
        )}
        <Form
          method="post"
          {...getFormProps(form)}
          className="grid grid-cols-1 gap-md"
        >
          <TextFieldWrapper>
            <div className="flex justify-between items-baseline ">
              <Label className="text-inherit" htmlFor="password">
                Email
              </Label>
            </div>
            <TextField
              {...getInputProps(fields.email, { type: 'email' })}
              errors={fields.email.errors}
            />
          </TextFieldWrapper>

          <Button
            variant="primary"
            type="submit"
            className="mt-md"
            disabled={isPending}
          >
            {isPending ? 'Loading...' : 'Request Password Reset'}
          </Button>
        </Form>
      </CardBody>
    </CardBase>
  );
}

function isSuccess(
  data: { success: boolean } | undefined
): data is { success: true; message: string } {
  return data?.success === true;
}
